/**
 * Schema validation dispatcher — switches between the client-side mock
 * validator and the real backend `POST /api/validate/schema` endpoint.
 *
 * Selection is controlled by env flag `VITE_SCHEMA_VALIDATION_USE_MOCK`:
 *   - unset / "true"  → mock (default, no network)  ← prototype default
 *   - "false"         → real backend call
 *
 * Both paths return the SAME result shape (the backend endpoint was designed
 * to match `runMockSchemaValidation` exactly — see
 * FederateX_agent_sync/Backend_To_Frontend/2026-05-26_backend_b1_b4_endpoint_contract_draft.md §B4):
 *   { overall, blocked, code, abortReason, summary, expectedSchema,
 *     schemaHeader, counts, siteResults[ {..., findings[] } ], source, ranAt }
 *
 * The mock fallback is always kept. This module does NOT trigger any live API
 * testing on its own — the real branch only runs when the flag is explicitly
 * flipped to "false" by whoever is doing integration.
 */

import { apiPost } from "./api";
import { runMockSchemaValidation } from "./mockSchemaValidation";

/** True when schema validation should use the client-side mock (default). */
export const isSchemaValidationMock = () => {
  const flag =
    (typeof import.meta !== "undefined" &&
      import.meta.env?.VITE_SCHEMA_VALIDATION_USE_MOCK) ??
    "true";
  return String(flag) !== "false";
};

/**
 * Normalize whatever the backend returns into the canonical result shape and
 * guarantee the fields the UI relies on exist (defensive; the contract already
 * matches the mock shape). Accepts a bare object or a `{ data: {...} }` wrapper.
 */
const normalizeRealResponse = (raw, source) => {
  const r = raw && raw.data && raw.data.overall ? raw.data : raw || {};
  const overall = r.overall || (r.passed ? "pass" : r.blocked ? "fail" : "not_ready");
  const counts = r.counts || { pass: 0, warn: 0, fail: 0 };
  return {
    overall,
    passed: overall === "pass",
    blocked: typeof r.blocked === "boolean" ? r.blocked : overall === "fail",
    code: r.code || (overall === "pass" ? "ok" : "has_findings"),
    abortReason:
      r.abortReason ?? (overall === "fail" ? "DATA_SCHEMA_VALIDATION_FAIL_PRELAUNCH" : null),
    // dev-0.7.3+ — when blocked=true, backend may emit `abortStage` (e.g.
    // "l2_data" for tabular data fails, "l2_contract" for imaging schema
    // contract fails per dev-0.7.4). Pass it through so
    // SchemaValidationDetailsCard can render the friendly sub-label.
    // Older backends don't emit this; the card falls back to "prelaunch".
    abortStage: r.abortStage ?? null,
    summary: r.summary || "",
    expectedSchema: r.expectedSchema ?? r.schemaHeader?.schema_id ?? null,
    schemaHeader: r.schemaHeader ?? null,
    counts,
    // Demo-only rename for site_smh_01 — see AppContext.fetchSites for
    // the original rationale. Mirrored here because validate/schema
    // response carries its own `site_display_name` echo (not re-looked-up
    // by FE), so the AppContext sites-array rename alone would not catch
    // SchemaPreCheckPanel / Federated Data Review card.
    siteResults: Array.isArray(r.siteResults)
      ? r.siteResults.map((sr) =>
          sr?.site_id === 'site_smh_01'
            ? { ...sr, site_display_name: 'South Metro Hospital' }
            : sr
        )
      : [],
    source: r.source || source || "pre_check",
    ranAt: r.ranAt || new Date().toISOString(),
  };
};

/**
 * Run schema validation against the current selection.
 *
 * @param {{
 *   selectedSites: Array<{site_id:string, capability_id:string}>,
 *   siteCapabilities: Record<string, Array<object>>,
 *   sites: Array<object>,
 *   processingType: string,
 *   source: "pre_check" | "final",
 * }} args
 * @returns {Promise<object>} result in the shared canonical shape
 */
export const runSchemaValidation = async ({
  selectedSites,
  siteCapabilities,
  sites,
  processingType,
  source,
}) => {
  const safeSource = source === "final" ? "final" : "pre_check";

  // ── Mock path (default) ────────────────────────────────────────────────
  if (isSchemaValidationMock()) {
    // runMockSchemaValidation is synchronous; awaiting a plain value is fine.
    return runMockSchemaValidation({
      selectedSites,
      siteCapabilities,
      sites,
      processingType,
      source: safeSource,
    });
  }

  // ── Real path — POST /api/validate/schema ──────────────────────────────
  // API_BASE already includes the `/api` prefix, so the endpoint is
  // `/validate/schema` → `${API_BASE}/validate/schema`.
  const completed = (selectedSites || []).filter(
    (s) => s && s.site_id && s.capability_id
  );
  const body = {
    sites: completed.map((s) => ({
      site_id: s.site_id,
      capability_id: s.capability_id,
    })),
    processing_type: processingType || null,
    source: safeSource,
  };

  try {
    const raw = await apiPost("/validate/schema", body);
    return normalizeRealResponse(raw, safeSource);
  } catch (error) {
    // Transport/auth/registry error (non-200). Per contract, a *failing
    // dataset* is a 200 with overall:"fail" — so reaching here means the
    // validation service itself errored. Surface it as a non-blocking
    // "not_ready"-style result (do NOT hard-block Start) and let the UI show
    // the message. The mock path is unaffected and remains the default.
    console.error("[schemaValidation] real /validate/schema failed:", error);
    return {
      overall: "not_ready",
      passed: false,
      blocked: false,
      code: "validation_error",
      abortReason: null,
      summary: `Schema validation service error: ${error?.message || error}`,
      expectedSchema: null,
      schemaHeader: null,
      counts: { pass: 0, warn: 0, fail: 0 },
      siteResults: [],
      source: safeSource,
      ranAt: new Date().toISOString(),
    };
  }
};
